import { useMemo, ReactNode } from "react";
import Button from "../core/Button";
import PageTitle from "../common/PageTitle";
import { DataGenieIcon } from "src/components/icons";
import { STEPS } from "./constants";

interface StepInfo {
  name: number;
  pageTitle: string;
  breadcrumbLabel: string;
  navigateToPermitted: boolean;
}

interface StepViewProps {
  onPrevClick: () => void;
  onNextClick: () => void;
  onCancelClick: () => void;
  onStepClick: (step: number) => void;
  currentStep: number;
  currentStepInfo: StepInfo;
  isLastStep: boolean;
  children: ReactNode;
  stepsInfo: StepInfo[];
  userCredits?: number;
  nextStep?: number;
}

const StepView = ({
  onPrevClick,
  onNextClick,
  onCancelClick,
  currentStep,
  currentStepInfo,
  isLastStep,
  children,
  stepsInfo,
  userCredits,
  nextStep,
}: StepViewProps) => {

  const isNextDisabled = useMemo(
    () => {
      const targetStep = nextStep ?? currentStep + 1;
      return !(stepsInfo || []).find(({ name }) => name === targetStep)
        ?.navigateToPermitted;
    },
    [currentStep, stepsInfo, nextStep]
  );

  const isPreviousDisabled = useMemo(
    () => currentStep === STEPS.configureUpload,
    [currentStep]
  );

  return (
    <section className="step-view h-screen md:h-screen mb-[80px]">
      <PageTitle
        titleIcon={<DataGenieIcon style={{ fontSize: "19px" }} />}
        title={currentStepInfo.pageTitle}
        subtitle="Data Genie"
        rightSideContent={
          userCredits !== undefined && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
              <span className="text-xs font-bold text-gray-secondary uppercase tracking-wide">Credits Remaining:</span>
              <span className="text-base font-semibold text-gray-primary">{userCredits.toLocaleString()}</span>
            </div>
          )
        }
      />
      <main className="pt-3">{children}</main>
      <section className="sticky right-0 bottom-0 h-[72px] mt-4 md:mt-8 z-10">
        <div className="relative flex border-t-gray-300 border-t-2 h-[72px] w-full bottom-0 bg-white items-center justify-center gap-4">
          <Button variant="outline" onClick={onCancelClick} className="absolute left-0">Cancel</Button>
          <Button disabled={isPreviousDisabled} onClick={onPrevClick}>
            Back
          </Button>
          {!isLastStep && (
            <Button
              onClick={onNextClick}
              disabled={isNextDisabled}
            >
              Next
            </Button>
          )}
        </div>
      </section>
    </section>
  );
};

export default StepView;
